<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, Link } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import BaseIcon from "@/Components/BaseIcon.vue";
import axios from 'axios';
import {
    mdiBookOpenVariant,
    mdiCheckCircle,
    mdiClockOutline,
    mdiAlertCircleOutline,
    mdiMagnify,
    mdiViewGridOutline,
    mdiFormatListBulleted,
    mdiStar,
    mdiTrophyOutline,
    mdiTrendingUp,
    mdiTrendingDown,
    mdiArrowRight,
    mdiSchool,
    mdiClose,
    mdiCheckboxMarkedCircleOutline,
    mdiCalendarClock
} from "@mdi/js";

const props = defineProps({
    canRead: Boolean,
    canWrite: Boolean,
    canDelete: Boolean,
    courses: {
        type: Array,
        default: () => []
    },
    level_label: {
        type: String,
        default: ''
    },
    bestCourse: {
        type: Object,
        default: () => ({ id: null, label: '-', score: 0 })
    },
    worstCourse: {
        type: Object,
        default: () => ({ id: null, label: '-', score: 0 })
    }
});

// Estados reactivos de tareas
const loadingTasks = ref(true);
const countTareaWithResp = ref(0);
const countTareaWithOutResp = ref(0);
const arrayTareas = ref([]);
const arrayTareasPendientes = ref([]);
const arrayRespuestas = ref([]);

// Filtros y vista
const searchQuery = ref('');
const selectedArea = ref('all');
const filterTasksOnly = ref(false);
const viewMode = ref('grid'); // 'grid' | 'list'

onMounted(() => {
    try {
        const savedView = localStorage.getItem('student_courses_view');
        if (savedView === 'grid' || savedView === 'list') {
            viewMode.value = savedView;
        }
    } catch (e) {
        // LocalStorage no disponible
    }
    listarTareas();
});

const setViewMode = (mode) => {
    viewMode.value = mode;
    try {
        localStorage.setItem('student_courses_view', mode);
    } catch (e) {}
};

const listarTareas = () => {
    loadingTasks.value = true;
    axios.get('/task/dashboard', { timeout: 10000 })
        .then((response) => {
            const res = response.data || {};
            arrayTareas.value = res.tareas || [];
            arrayRespuestas.value = res.respuestas || [];
            arrayTareasPendientes.value = res.tareasPendientes || [];
            countTareaWithOutResp.value = res.countTareaWithOutResp ?? 0;
            countTareaWithResp.value = res.countTareaWithResp ?? 0;
        })
        .catch((error) => {
            console.warn('Error al cargar tareas en cursos:', error);
        })
        .finally(() => {
            loadingTasks.value = false;
        });
};

// Mapa optimizado O(N) para evitar filtros reactivos costosos en el template
const tasksByCourse = computed(() => {
    const map = {};
    (props.courses || []).forEach(c => {
        map[c.id] = {
            tasks: [],
            responses: [],
            pending: [],
            percent: 0
        };
    });

    (arrayTareas.value || []).forEach(t => {
        if (map[t.id_course]) {
            map[t.id_course].tasks.push(t);
        }
    });

    (arrayRespuestas.value || []).forEach(r => {
        if (map[r.id_course]) {
            map[r.id_course].responses.push(r);
        }
    });

    (arrayTareasPendientes.value || []).forEach(p => {
        if (map[p.id_course]) {
            map[p.id_course].pending.push(p);
        }
    });

    Object.keys(map).forEach(courseId => {
        const item = map[courseId];
        const total = item.tasks.length;
        const delivered = item.responses.length;
        item.percent = total > 0 ? Math.min(100, Math.round((delivered / total) * 100)) : 100;
    });

    return map;
});

// Lista de áreas disponibles para filtrado
const availableAreas = computed(() => {
    const set = new Set();
    (props.courses || []).forEach(c => {
        if (c.label_area) set.add(c.label_area.trim());
    });
    return Array.from(set).sort();
});

// Cursos filtrados
const filteredCourses = computed(() => {
    let list = props.courses || [];

    if (selectedArea.value !== 'all') {
        list = list.filter(c => (c.label_area || '').trim() === selectedArea.value);
    }

    if (filterTasksOnly.value) {
        list = list.filter(c => {
            const stats = tasksByCourse.value[c.id];
            return stats && stats.pending.length > 0;
        });
    }

    if (searchQuery.value.trim()) {
        const q = searchQuery.value.toLowerCase().trim();
        list = list.filter(c =>
            (c.label && c.label.toLowerCase().includes(q)) ||
            (c.label_area && c.label_area.toLowerCase().includes(q)) ||
            (c.ref && c.ref.toLowerCase().includes(q))
        );
    }

    return list;
});

// Helper de estilos temáticos según el área del curso
const getAreaTheme = (area = '') => {
    const a = (area || '').toLowerCase();
    if (a.includes('matem') || a.includes('álgeb') || a.includes('geom') || a.includes('arit')) {
        return {
            badge: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
            bar: 'bg-blue-600',
            iconBg: 'bg-blue-600',
            gradient: 'from-blue-600 to-indigo-600'
        };
    }
    if (a.includes('comunic') || a.includes('leng') || a.includes('liter')) {
        return {
            badge: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
            bar: 'bg-purple-600',
            iconBg: 'bg-purple-600',
            gradient: 'from-purple-600 to-fuchsia-600'
        };
    }
    if (a.includes('cien') || a.includes('biol') || a.includes('quím') || a.includes('físic') || a.includes('ambien')) {
        return {
            badge: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
            bar: 'bg-emerald-600',
            iconBg: 'bg-emerald-600',
            gradient: 'from-emerald-600 to-teal-600'
        };
    }
    if (a.includes('hist') || a.includes('geog') || a.includes('soci') || a.includes('cívic') || a.includes('desarr')) {
        return {
            badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
            bar: 'bg-amber-500',
            iconBg: 'bg-amber-600',
            gradient: 'from-amber-500 to-orange-600'
        };
    }
    if (a.includes('ingl') || a.includes('idiom') || a.includes('foreign')) {
        return {
            badge: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
            bar: 'bg-indigo-600',
            iconBg: 'bg-indigo-600',
            gradient: 'from-indigo-600 to-sky-600'
        };
    }
    if (a.includes('arte') || a.includes('educación fís') || a.includes('depor') || a.includes('músic')) {
        return {
            badge: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
            bar: 'bg-rose-500',
            iconBg: 'bg-rose-600',
            gradient: 'from-rose-500 to-pink-600'
        };
    }
    return {
        badge: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
        bar: 'bg-sky-600',
        iconBg: 'bg-slate-700',
        gradient: 'from-slate-700 to-gray-800'
    };
};

// Formato y cálculo de vencimiento inteligente
const formatDueDate = (dateStr) => {
    if (!dateStr) return { label: '-', isUrgent: false, isToday: false };
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60));

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    const timeFormatted = `${hours}:${mins}`;

    if (diffHours < 0) {
        return { label: `Venció el ${day}/${month}`, isUrgent: true, isOverdue: true };
    }
    if (diffHours <= 24) {
        return { label: `Hoy a las ${timeFormatted}`, isUrgent: true, isToday: true };
    }
    if (diffHours <= 48) {
        return { label: `Mañana a las ${timeFormatted}`, isUrgent: true, isTomorrow: true };
    }
    return { label: `${day}/${month} a las ${timeFormatted}`, isUrgent: false };
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedArea.value = 'all';
    filterTasksOnly.value = false;
};
</script>

<template>
    <Head title="Mis Cursos" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader
                :title="'Mis cursos' + (level_label ? ' | ' + level_label : '')"
                subtitle="Gestión académica, seguimiento de materias y entrega de tareas"
                icon="solar:book-bookmark-bold-duotone"
                color="blue"
            >
                <!-- Switcher de Vista en Header -->
                <div class="inline-flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                    <button
                        type="button"
                        @click="setViewMode('grid')"
                        :class="[
                            'px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all duration-150',
                            viewMode === 'grid'
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        ]"
                        title="Vista en cuadrícula"
                    >
                        <BaseIcon :path="mdiViewGridOutline" :size="16" />
                        <span class="hidden sm:inline">Tarjetas</span>
                    </button>
                    <button
                        type="button"
                        @click="setViewMode('list')"
                        :class="[
                            'px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all duration-150',
                            viewMode === 'list'
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        ]"
                        title="Vista en lista"
                    >
                        <BaseIcon :path="mdiFormatListBulleted" :size="16" />
                        <span class="hidden sm:inline">Lista</span>
                    </button>
                </div>
            </PremiumPageHeader>
        </template>

        <div class="space-y-6 pb-12">
            <!-- 1. KPI & Hero Cards -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Card 1: Total Cursos -->
                <div class="bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm flex items-center gap-4 transition-all duration-200 hover:shadow-md">
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                        <BaseIcon :path="mdiBookOpenVariant" :size="24" />
                    </div>
                    <div class="min-w-0 flex-1">
                        <p class="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Asignaturas inscritas</p>
                        <h4 class="text-2xl font-black text-gray-900 dark:text-white leading-tight mt-0.5">
                            {{ courses?.length || 0 }}
                        </h4>
                    </div>
                </div>

                <!-- Card 2: Tareas Pendientes -->
                <div class="bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm flex items-center gap-4 transition-all duration-200 hover:shadow-md">
                    <div
                        :class="[
                            'w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md shrink-0 transition-colors',
                            countTareaWithOutResp > 0
                                ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20'
                                : 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/20'
                        ]"
                    >
                        <BaseIcon :path="countTareaWithOutResp > 0 ? mdiClockOutline : mdiCheckCircle" :size="24" />
                    </div>
                    <div class="min-w-0 flex-1">
                        <p class="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Tareas pendientes</p>
                        <div class="flex items-center gap-2 mt-0.5">
                            <h4 class="text-2xl font-black text-gray-900 dark:text-white leading-tight">
                                {{ countTareaWithOutResp }}
                            </h4>
                            <span
                                v-if="countTareaWithOutResp === 0"
                                class="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 rounded-full"
                            >
                                ¡Al día!
                            </span>
                            <span
                                v-else
                                class="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 rounded-full"
                            >
                                Por entregar
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Card 3: Mejor Calificado -->
                <Link
                    :href="bestCourse?.id ? route('student-course-card', { id: bestCourse.id, back_link: 'board' }) : '#'"
                    :class="[
                        'bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm flex items-center gap-4 transition-all duration-200 group',
                        bestCourse?.id ? 'hover:shadow-md hover:border-emerald-200 dark:hover:border-emerald-800/50 cursor-pointer' : 'opacity-80'
                    ]"
                >
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md shadow-emerald-500/20 shrink-0 group-hover:scale-105 transition-transform">
                        <BaseIcon :path="mdiTrophyOutline" :size="24" />
                    </div>
                    <div class="min-w-0 flex-1">
                        <div class="flex items-center justify-between gap-1">
                            <p class="text-xs font-medium text-emerald-600 dark:text-emerald-400 font-semibold truncate">Mejor calificado</p>
                            <span v-if="bestCourse?.score > 0" class="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.2 rounded">
                                {{ bestCourse.score }}
                            </span>
                        </div>
                        <h4 class="text-sm font-bold text-gray-900 dark:text-white truncate mt-0.5 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                            {{ bestCourse?.label || 'Sin registro aún' }}
                        </h4>
                    </div>
                </Link>

                <!-- Card 4: Curso por Reforzar -->
                <Link
                    :href="worstCourse?.id ? route('student-course-card', { id: worstCourse.id, back_link: 'board' }) : '#'"
                    :class="[
                        'bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm flex items-center gap-4 transition-all duration-200 group',
                        worstCourse?.id ? 'hover:shadow-md hover:border-rose-200 dark:hover:border-rose-800/50 cursor-pointer' : 'opacity-80'
                    ]"
                >
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white shadow-md shadow-rose-500/20 shrink-0 group-hover:scale-105 transition-transform">
                        <BaseIcon :path="mdiTrendingUp" :size="24" />
                    </div>
                    <div class="min-w-0 flex-1">
                        <div class="flex items-center justify-between gap-1">
                            <p class="text-xs font-medium text-rose-600 dark:text-rose-400 font-semibold truncate">Oportunidad de mejora</p>
                            <span v-if="worstCourse?.score > 0" class="text-xs font-bold text-rose-700 dark:text-rose-300 bg-rose-100 dark:bg-rose-900/40 px-1.5 py-0.2 rounded">
                                {{ worstCourse.score }}
                            </span>
                        </div>
                        <h4 class="text-sm font-bold text-gray-900 dark:text-white truncate mt-0.5 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors">
                            {{ worstCourse?.label || 'Sin registro aún' }}
                        </h4>
                    </div>
                </Link>
            </div>

            <!-- 2. Filtros y Barra de Búsqueda -->
            <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm space-y-3">
                <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <!-- Buscador en tiempo real -->
                    <div class="relative flex-1">
                        <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                            <BaseIcon :path="mdiMagnify" :size="20" />
                        </div>
                        <input
                            v-model="searchQuery"
                            type="text"
                            placeholder="Buscar por curso o área curricular..."
                            class="w-full pl-10 pr-9 py-2 text-sm bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white transition-colors"
                        />
                        <button
                            v-if="searchQuery"
                            @click="searchQuery = ''"
                            type="button"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <BaseIcon :path="mdiClose" :size="18" />
                        </button>
                    </div>

                    <!-- Botón rápido: Sólo con pendientes -->
                    <div class="flex items-center gap-2">
                        <button
                            type="button"
                            @click="filterTasksOnly = !filterTasksOnly"
                            :class="[
                                'px-3.5 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 border transition-all duration-150',
                                filterTasksOnly
                                    ? 'bg-amber-500 text-white border-amber-600 shadow-sm shadow-amber-500/20'
                                    : 'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                            ]"
                        >
                            <BaseIcon :path="mdiClockOutline" :size="16" />
                            <span>Sólo pendientes</span>
                            <span
                                v-if="countTareaWithOutResp > 0"
                                :class="[
                                    'px-1.5 py-0.5 text-[10px] font-bold rounded-full',
                                    filterTasksOnly ? 'bg-white text-amber-600' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                                ]"
                            >
                                {{ countTareaWithOutResp }}
                            </span>
                        </button>
                    </div>
                </div>

                <!-- Chips de Áreas Curriculares -->
                <div v-if="availableAreas.length > 1" class="flex items-center gap-2 overflow-x-auto pb-1 text-xs scrollbar-thin">
                    <span class="text-gray-400 font-medium whitespace-nowrap pl-1">Área:</span>
                    <button
                        type="button"
                        @click="selectedArea = 'all'"
                        :class="[
                            'px-3 py-1 rounded-lg whitespace-nowrap font-medium transition-all duration-150',
                            selectedArea === 'all'
                                ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        ]"
                    >
                        Todas ({{ courses?.length || 0 }})
                    </button>
                    <button
                        v-for="area in availableAreas"
                        :key="area"
                        type="button"
                        @click="selectedArea = area"
                        :class="[
                            'px-3 py-1 rounded-lg whitespace-nowrap font-medium transition-all duration-150',
                            selectedArea === area
                                ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        ]"
                    >
                        {{ area }}
                    </button>
                </div>
            </div>

            <!-- 3. Contenido Principal: Grid o Lista -->
            <!-- Skeleton Loader durante la carga inicial de tareas -->
            <div v-if="loadingTasks" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 animate-pulse">
                <div v-for="n in 6" :key="n" class="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/60 shadow-sm space-y-4">
                    <div class="flex items-center gap-3">
                        <div class="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                        <div class="flex-1 space-y-2">
                            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                            <div class="h-3 bg-gray-100 dark:bg-gray-700/60 rounded w-1/2"></div>
                        </div>
                    </div>
                    <div class="space-y-2 pt-2">
                        <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                        <div class="h-2.5 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                    </div>
                    <div class="h-9 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                </div>
            </div>

            <!-- Empty State si no hay resultados -->
            <div
                v-else-if="filteredCourses.length === 0"
                class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 p-12 text-center shadow-sm"
            >
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <BaseIcon :path="mdiBookOpenVariant" :size="32" />
                </div>
                <h3 class="text-lg font-bold text-gray-900 dark:text-white">No se encontraron cursos</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                    No hay asignaturas que coincidan con los filtros seleccionados actualmente.
                </p>
                <div class="mt-5">
                    <button
                        type="button"
                        @click="clearFilters"
                        class="inline-flex items-center px-4 py-2 text-xs font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-500/20 transition-colors"
                    >
                        Restablecer filtros
                    </button>
                </div>
            </div>

            <!-- VISTA CUADRÍCULA (Cards) -->
            <div v-else-if="viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                <div
                    v-for="curso in filteredCourses"
                    :key="curso.id"
                    class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm hover:shadow-xl transition-all duration-200 flex flex-col justify-between overflow-hidden group hover:-translate-y-1"
                >
                    <!-- Header Card con área e icono -->
                    <div class="p-5 pb-3">
                        <div class="flex items-start justify-between gap-3 mb-3">
                            <div
                                :class="[
                                    'w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md shrink-0 transition-transform group-hover:scale-105',
                                    getAreaTheme(curso.label_area).iconBg
                                ]"
                            >
                                <BaseIcon :path="mdiBookOpenVariant" :size="22" />
                            </div>

                            <span
                                :class="[
                                    'px-2.5 py-1 text-[11px] font-semibold rounded-lg border text-right truncate max-w-[150px]',
                                    getAreaTheme(curso.label_area).badge
                                ]"
                                :title="curso.label_area"
                            >
                                {{ curso.label_area || 'General' }}
                            </span>
                        </div>

                        <!-- Nombre del curso -->
                        <Link
                            :href="route('student-course-card', { id: curso.id, back_link: 'board' })"
                            class="block group/title"
                        >
                            <h3 class="text-base font-bold text-gray-900 dark:text-white leading-snug group-hover/title:text-blue-600 dark:group-hover/title:text-blue-400 transition-colors line-clamp-2">
                                {{ curso.label }}
                            </h3>
                            <p v-if="curso.ref" class="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">
                                Cód: {{ curso.ref }}
                            </p>
                        </Link>
                    </div>

                    <!-- Progreso de Tareas -->
                    <div class="px-5 py-2.5 border-t border-b border-gray-50 dark:border-gray-700/40 bg-gray-50/50 dark:bg-gray-900/20 space-y-2">
                        <div class="flex items-center justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400 font-medium">Tareas entregadas</span>
                            <span class="font-bold text-gray-700 dark:text-gray-300">
                                {{ tasksByCourse[curso.id]?.responses?.length || 0 }} / {{ tasksByCourse[curso.id]?.tasks?.length || 0 }}
                                <span class="text-[11px] text-gray-400">({{ tasksByCourse[curso.id]?.percent || 0 }}%)</span>
                            </span>
                        </div>

                        <!-- Barra de progreso estilizada -->
                        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div
                                :class="[
                                    'h-full rounded-full transition-all duration-500',
                                    (tasksByCourse[curso.id]?.percent || 0) === 100
                                        ? 'bg-emerald-500'
                                        : getAreaTheme(curso.label_area).bar
                                ]"
                                :style="{ width: `${tasksByCourse[curso.id]?.percent || 0}%` }"
                            ></div>
                        </div>

                        <!-- Alertas de vencimiento -->
                        <div class="pt-1">
                            <!-- Si tiene tareas pendientes -->
                            <div v-if="tasksByCourse[curso.id]?.pending?.length > 0" class="space-y-1.5">
                                <div class="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-semibold">
                                    <BaseIcon :path="mdiClockOutline" :size="14" />
                                    <span>Próximas entregas:</span>
                                </div>
                                <div
                                    v-for="tp in tasksByCourse[curso.id].pending.slice(0, 2)"
                                    :key="tp.id"
                                    class="text-[11px] bg-white dark:bg-gray-800 p-2 rounded-lg border border-amber-100 dark:border-amber-900/30 text-gray-700 dark:text-gray-300 flex items-center justify-between gap-1 shadow-2xs"
                                >
                                    <span class="truncate font-medium" :title="tp.label">{{ tp.label }}</span>
                                    <span
                                        :class="[
                                            'px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap font-semibold shrink-0',
                                            formatDueDate(tp.end_date).isUrgent
                                                ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300'
                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                        ]"
                                    >
                                        {{ formatDueDate(tp.end_date).label }}
                                    </span>
                                </div>
                                <p v-if="tasksByCourse[curso.id].pending.length > 2" class="text-[10px] text-gray-400 text-right">
                                    +{{ tasksByCourse[curso.id].pending.length - 2 }} tarea(s) más
                                </p>
                            </div>

                            <!-- Si no tiene pendientes -->
                            <div v-else class="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 py-1">
                                <BaseIcon :path="mdiCheckCircle" :size="15" />
                                <span class="font-medium">Sin tareas pendientes</span>
                            </div>
                        </div>
                    </div>

                    <!-- Footer: Acción de ingreso -->
                    <div class="p-4 pt-3">
                        <Link
                            :href="route('student-course-card', { id: curso.id, back_link: 'board' })"
                            class="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm shadow-blue-500/20 active:scale-[0.98] transition-all"
                        >
                            <span>Ingresar al curso</span>
                            <BaseIcon :path="mdiArrowRight" :size="16" class="transition-transform group-hover:translate-x-1" />
                        </Link>
                    </div>
                </div>
            </div>

            <!-- VISTA LISTA (Tabla moderna responsiva) -->
            <div v-else class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/60 shadow-sm overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="w-full text-left text-sm text-gray-600 dark:text-gray-300">
                        <thead class="bg-gray-50/80 dark:bg-gray-900/50 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700/60">
                            <tr>
                                <th scope="col" class="py-3.5 px-4">Asignatura</th>
                                <th scope="col" class="py-3.5 px-4">Área</th>
                                <th scope="col" class="py-3.5 px-4">Progreso de Tareas</th>
                                <th scope="col" class="py-3.5 px-4">Próximos Vencimientos</th>
                                <th scope="col" class="py-3.5 px-4 text-right">Acción</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
                            <tr
                                v-for="curso in filteredCourses"
                                :key="curso.id"
                                class="hover:bg-gray-50/70 dark:hover:bg-gray-700/30 transition-colors"
                            >
                                <!-- Nombre del Curso -->
                                <td class="py-4 px-4">
                                    <div class="flex items-center gap-3">
                                        <div
                                            :class="[
                                                'w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-xs shrink-0',
                                                getAreaTheme(curso.label_area).iconBg
                                            ]"
                                        >
                                            <BaseIcon :path="mdiBookOpenVariant" :size="20" />
                                        </div>
                                        <div class="min-w-0">
                                            <Link
                                                :href="route('student-course-card', { id: curso.id, back_link: 'board' })"
                                                class="font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm"
                                            >
                                                {{ curso.label }}
                                            </Link>
                                            <p v-if="curso.ref" class="text-xs text-gray-400 font-mono">
                                                {{ curso.ref }}
                                            </p>
                                        </div>
                                    </div>
                                </td>

                                <!-- Área -->
                                <td class="py-4 px-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2.5 py-1 text-xs font-semibold rounded-lg border',
                                            getAreaTheme(curso.label_area).badge
                                        ]"
                                    >
                                        {{ curso.label_area || 'General' }}
                                    </span>
                                </td>

                                <!-- Progreso -->
                                <td class="py-4 px-4 min-w-[180px]">
                                    <div class="space-y-1.5">
                                        <div class="flex items-center justify-between text-xs">
                                            <span class="text-gray-500 dark:text-gray-400">
                                                {{ tasksByCourse[curso.id]?.responses?.length || 0 }} de {{ tasksByCourse[curso.id]?.tasks?.length || 0 }}
                                            </span>
                                            <span class="font-bold text-gray-700 dark:text-gray-300">
                                                {{ tasksByCourse[curso.id]?.percent || 0 }}%
                                            </span>
                                        </div>
                                        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                :class="[
                                                    'h-full rounded-full transition-all duration-300',
                                                    (tasksByCourse[curso.id]?.percent || 0) === 100
                                                        ? 'bg-emerald-500'
                                                        : getAreaTheme(curso.label_area).bar
                                                ]"
                                                :style="{ width: `${tasksByCourse[curso.id]?.percent || 0}%` }"
                                            ></div>
                                        </div>
                                    </div>
                                </td>

                                <!-- Próximos Vencimientos -->
                                <td class="py-4 px-4">
                                    <div v-if="tasksByCourse[curso.id]?.pending?.length > 0" class="space-y-1 max-w-xs">
                                        <div
                                            v-for="tp in tasksByCourse[curso.id].pending.slice(0, 1)"
                                            :key="tp.id"
                                            class="text-xs text-gray-700 dark:text-gray-300 truncate"
                                        >
                                            <span class="font-medium text-amber-600 dark:text-amber-400 mr-1">•</span>
                                            <span :title="tp.label">{{ tp.label }}</span>
                                            <span class="text-gray-400 text-[11px] block sm:inline sm:ml-1">
                                                ({{ formatDueDate(tp.end_date).label }})
                                            </span>
                                        </div>
                                        <span v-if="tasksByCourse[curso.id].pending.length > 1" class="text-[11px] text-gray-400">
                                            +{{ tasksByCourse[curso.id].pending.length - 1 }} pendiente(s) más
                                        </span>
                                    </div>
                                    <div v-else class="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                                        <BaseIcon :path="mdiCheckCircle" :size="14" />
                                        <span>Al día</span>
                                    </div>
                                </td>

                                <!-- Botón Acción -->
                                <td class="py-4 px-4 text-right whitespace-nowrap">
                                    <Link
                                        :href="route('student-course-card', { id: curso.id, back_link: 'board' })"
                                        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                                    >
                                        <span>Entrar</span>
                                        <BaseIcon :path="mdiArrowRight" :size="14" />
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </BreezeAuthenticatedLayout>
</template>
